import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

// A small "i" affordance that reveals a short note on hover.
//
// Hover alone is not enough — it never fires on touch and is invisible to a
// keyboard — so the trigger is a real <button>: hover and focus both open it,
// tap toggles it, Escape and an outside tap close it. `title` is deliberately
// NOT set, or touch devices would get the native tooltip on top of this one.
//
// The panel sits above the icon. Callers render this inside cards with
// `overflow-y-auto`, so keep it short (one line) — a tall panel would clip.
//
// `interactive` is for a panel the pointer has to reach: it wraps onto several
// lines and stops ignoring clicks, so the content can hold a link. It also
// means the open/close handlers have to live on the wrapper rather than the
// button — with them on the button, moving the mouse off it to reach the link
// closes the panel before the click lands.
//
// `placement` and `align` exist because this sits in toolbars: a panel centred
// on an icon in the top-right corner is clipped twice over — off the top of
// the viewport by opening upward, and off the right edge by centring a wide
// panel on something 14px wide. Anchoring it below and to the right keeps it
// on screen. Defaults preserve the original above-and-centred behaviour.
const InfoTooltip = ({
  label,
  children,
  interactive = false,
  placement = 'top',
  align = 'center',
  tone = 'default',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const id = useId();

  // Anchoring alone isn't enough on a narrow screen: a 248px panel hung off an
  // icon 198px from the left edge of a 375px viewport still overflows, just to
  // the left instead of the right. Nudge it back inside once it's laid out.
  // CSS can't express this — the correction depends on where the icon happens
  // to sit — so it's measured.
  //
  // Measured from the ANCHOR, never from the panel. Reading the panel's own
  // rect feeds the shift back into the next measurement: each pass moves it
  // further and re-triggers the effect, and for a panel with no layout box at
  // all (inside a `hidden lg:flex` toolbar, say) every pass reads zeros and the
  // correction grows without ever settling — an infinite render loop that takes
  // the page down. The anchor's position doesn't depend on the shift, so one
  // pass is enough.
  useLayoutEffect(() => {
    if (!open || !interactive) {
      setShift(0);
      return;
    }
    const wrap = wrapRef.current;
    const panel = panelRef.current;
    if (!wrap || !panel) return;

    // offsetWidth ignores transforms. Zero means the tooltip isn't being laid
    // out — nothing to correct, and nothing meaningful to measure.
    const width = panel.offsetWidth;
    if (!width) {
      setShift(0);
      return;
    }

    const GUTTER = 8;
    const anchor = wrap.getBoundingClientRect();
    const left =
      align === 'right'
        ? anchor.right - width
        : anchor.left + anchor.width / 2 - width / 2;
    const right = left + width;

    if (left < GUTTER) setShift(GUTTER - left);
    else if (right > window.innerWidth - GUTTER)
      setShift(window.innerWidth - GUTTER - right);
    else setShift(0);
  }, [open, interactive, align]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex items-center align-middle ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={interactive ? undefined : close}
        onClick={(e) => {
          // Inside clickable rows (the BookingBubble total toggles a
          // breakdown), so never let the tap reach the parent.
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={[
          'inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-full transition-colors',
          // `light` is for dark cards, where the default muted grey all but
          // disappears.
          tone === 'light'
            ? 'text-white/70 hover:text-white'
            : 'text-muted hover:text-ink',
        ].join(' ')}
      >
        <InformationCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      {open && (
        <span
          id={id}
          ref={panelRef}
          role="tooltip"
          // Composed with the centring translate rather than replacing it —
          // an inline transform overrides the class entirely, so align=center
          // would jump half its width to the right without the -50% here.
          style={
            shift
              ? {
                  transform:
                    align === 'right'
                      ? `translateX(${shift}px)`
                      : `translateX(calc(-50% + ${shift}px))`,
                }
              : undefined
          }
          className={[
            'absolute z-50 rounded-md bg-ink text-white font-medium shadow-lift',
            placement === 'bottom' ? 'top-full mt-1.5' : 'bottom-full mb-1.5',
            align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2',
            interactive
              ? 'w-[248px] px-3 py-2.5 text-[11.5px] leading-[1.45] text-left ring-1 ring-white/10'
              : 'px-2 py-1 text-[11px] leading-none whitespace-nowrap pointer-events-none',
          ].join(' ')}
        >
          {children}
          <span
            aria-hidden="true"
            className={[
              'absolute border-4 border-transparent',
              placement === 'bottom'
                ? 'bottom-full -mb-px border-b-ink'
                : 'top-full -mt-px border-t-ink',
              // Follows the panel's anchor so the arrow still points at the
              // icon rather than at empty space beside it.
              align === 'right'
                ? 'right-1.5'
                : 'left-1/2 -translate-x-1/2',
            ].join(' ')}
          />
        </span>
      )}
    </span>
  );
};

export default InfoTooltip;
